import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  TextField,
  IconButton,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import ImportBatchReviewGrid from './ImportBatchReviewGrid';

interface ImportFormat {
  formatKey: string;
  displayName: string;
}

interface ImportBatch {
  id: string;
  fileName: string;
  formatKey: string;
  businessId?: string;
  businessName?: string;
  defaultTimePeriodId?: string;
  defaultTimePeriodName?: string;
  uploadedAtUtc: string;
  status: string;
  rowCount: number;
  stagedCount: number;
  matchedCount: number;
  errorSummary?: string;
}

// Whole percent, except that a batch with a few matches doesn't read 0%
// and one with a few left doesn't read 100%.
const formatMatchedPercent = (matched: number, staged: number): string => {
  if (staged === 0) return '-';
  const percent = (100 * matched) / staged;
  if (matched > 0 && percent < 1) return '<1%';
  if (matched < staged && percent > 99) return '>99%';
  return `${Math.round(percent)}%`;
};

// See api/DTOs/ImportUploadDto.cs (StageMissingRowsResultDto).
interface StageMissingResult {
  alreadyStaged: number;
  missing: number;
  missingByCategory: Record<string, number>;
  staged: number;
}

interface MissingHeader {
  expectedHeader: string;
  label: string;
}

// One worksheet of a file with several (a CCA Summary with a tab per client).
interface ImportSheet {
  name: string;
  rowCount: number;
  selected: boolean;
  looksCombined: boolean;
}

interface ImportPreview {
  rowCount: number;
  warnings: string[];
  sampleRows: { businessName?: string; farmName?: string; fieldName?: string; productName?: string; productCategoryName?: string; geneStack?: string; rate?: number }[];
  distinctGeneStackValues: string[];
  distinctProductCategoryValues: string[];
  requiresBusinessSelection: boolean;
  missingHeaders: MissingHeader[];
  availableHeaders: string[];
  sheets: ImportSheet[];
}

interface BusinessOption {
  id: string;
  name: string;
}

interface TimePeriodOption {
  id: string;
  name: string;
}

// From GET /api/import-uploads/time-periods; timePeriodId null = batches with none.
interface TimePeriodFilterOption {
  timePeriodId: string | null;
  name: string;
  batchCount: number;
}

// Checkbox key for the "No time period" option (real ones use the period's id).
const NO_TIME_PERIOD = 'none';

const DataImportManagement: React.FC = () => {
  const auth = useAuth();
  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${auth.tokens?.accessToken}` }), [auth.tokens?.accessToken]);

  const [batches, setBatches] = useState<ImportBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timePeriodFilterOptions, setTimePeriodFilterOptions] = useState<TimePeriodFilterOption[]>([]);
  // Nothing ticked = no filter, every batch.
  const [checkedTimePeriodKeys, setCheckedTimePeriodKeys] = useState<string[]>([]);

  const [reviewingBatch, setReviewingBatch] = useState<ImportBatch | null>(null);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [formats, setFormats] = useState<ImportFormat[]>([]);
  const [formatKey, setFormatKey] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [businesses, setBusinesses] = useState<BusinessOption[]>([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState<string | null>(null);
  const [timePeriods, setTimePeriods] = useState<TimePeriodOption[]>([]);
  const [selectedTimePeriodId, setSelectedTimePeriodId] = useState<string | null>(null);
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});
  // Ticked worksheets; null = the format's default pick, until a preview says what that is.
  const [selectedSheets, setSelectedSheets] = useState<string[] | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const [editingSeasonBatch, setEditingSeasonBatch] = useState<ImportBatch | null>(null);
  const [editingSeasonTimePeriodId, setEditingSeasonTimePeriodId] = useState<string | null>(null);
  const [savingSeason, setSavingSeason] = useState(false);
  const [seasonError, setSeasonError] = useState<string | null>(null);

  const [deletingBatch, setDeletingBatch] = useState<ImportBatch | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // "Stage missing rows": the batch's own file again, adding only rows it
  // doesn't have yet (see ImportUploadController.StageMissingRows).
  const [stagingBatch, setStagingBatch] = useState<ImportBatch | null>(null);
  const [stageFile, setStageFile] = useState<File | null>(null);
  const [stageResult, setStageResult] = useState<StageMissingResult | null>(null);
  const [stageBusy, setStageBusy] = useState(false);
  const [stageError, setStageError] = useState<string | null>(null);
  const stageFileRef = useRef<HTMLInputElement>(null);

  const fetchBatches = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      checkedTimePeriodKeys.forEach((key) => {
        if (key === NO_TIME_PERIOD) params.set('includeNoTimePeriod', 'true');
        else params.append('timePeriodIds', key);
      });
      // Options reload with the list, so counts stay right after an
      // upload, delete or season change.
      const [response, optionsResponse] = await Promise.all([
        fetch(`/api/import-uploads?${params}`, { headers: authHeaders() }),
        fetch('/api/import-uploads/time-periods', { headers: authHeaders() }),
      ]);
      if (!response.ok) throw new Error('Failed to fetch import batches');
      const data = await response.json();
      setBatches(data.items);
      if (optionsResponse.ok) setTimePeriodFilterOptions(await optionsResponse.json());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [authHeaders, checkedTimePeriodKeys]);

  useEffect(() => {
    if (auth.isSignedIn && auth.tokens?.accessToken) fetchBatches();
  }, [fetchBatches, auth.isSignedIn]);

  // A ticked period can lose its last batch (deleted or re-filed), which
  // removes its checkbox; untick it so it can't keep filtering invisibly.
  useEffect(() => {
    setCheckedTimePeriodKeys((prev) => {
      const next = prev.filter((key) => timePeriodFilterOptions.some((o) => (o.timePeriodId ?? NO_TIME_PERIOD) === key));
      return next.length === prev.length ? prev : next;
    });
  }, [timePeriodFilterOptions]);

  const toggleTimePeriodFilter = (key: string) => {
    setCheckedTimePeriodKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const openUploadDialog = async () => {
    setUploadOpen(true);
    setFile(null);
    setPreview(null);
    setUploadError(null);
    setSelectedBusinessId(null);
    setSelectedTimePeriodId(null);
    setColumnMapping({});
    setSelectedSheets(null);
    try {
      const [formatsRes, businessesRes, timePeriodsRes] = await Promise.all([
        fetch('/api/import-uploads/formats', { headers: authHeaders() }),
        fetch('/api/businesses/list', { headers: authHeaders() }),
        fetch('/api/time-periods', { headers: authHeaders() }),
      ]);
      if (formatsRes.ok) {
        const list: ImportFormat[] = await formatsRes.json();
        setFormats(list);
        setFormatKey(list[0]?.formatKey || '');
      }
      if (businessesRes.ok) setBusinesses(await businessesRes.json());
      if (timePeriodsRes.ok) setTimePeriods(await timePeriodsRes.json());
    } catch {
      // best-effort — dropdowns just stay empty if this fails
    }
  };

  const closeUploadDialog = () => {
    setUploadOpen(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  // sheets null = the format's default pick. A sheet toggle keeps the
  // current preview on screen so the checkboxes don't vanish meanwhile.
  const handlePreview = async (f: File, format: string, mapping: Record<string, string>, sheets: string[] | null = null) => {
    setUploadError(null);
    setPreviewing(true);
    if (sheets === null) setPreview(null);
    try {
      const form = new FormData();
      form.append('file', f);
      form.append('formatKey', format);
      if (Object.keys(mapping).length > 0) form.append('headerMapping', JSON.stringify(mapping));
      if (sheets !== null) form.append('sheets', JSON.stringify(sheets));
      const response = await fetch('/api/import-uploads/preview', { method: 'POST', headers: authHeaders(), body: form });
      const data: ImportPreview = await response.json();
      if (!response.ok) throw new Error((data as unknown as { error?: string }).error || 'Failed to preview file');
      setPreview(data);
      setSelectedSheets(data.sheets.length > 0 ? data.sheets.filter((s) => s.selected).map((s) => s.name) : null);
    } catch (err) {
      setPreview(null);
      setUploadError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setPreviewing(false);
    }
  };

  const handleFileChosen = (f: File) => {
    if (!f.name.toLowerCase().endsWith('.xlsx')) {
      setUploadError('Please select a .xlsx file');
      return;
    }
    setFile(f);
    setColumnMapping({});
    setSelectedSheets(null);
    if (formatKey) handlePreview(f, formatKey, {});
  };

  const handleFormatChange = (newFormat: string) => {
    setFormatKey(newFormat);
    setColumnMapping({});
    setSelectedSheets(null);
    if (file) handlePreview(file, newFormat, {});
  };

  const handleColumnMappingChange = (expectedHeader: string, actualColumn: string) => {
    const next = { ...columnMapping, [expectedHeader]: actualColumn };
    setColumnMapping(next);
    if (file && formatKey) handlePreview(file, formatKey, next, selectedSheets);
  };

  const handleSheetToggle = (name: string) => {
    if (!selectedSheets) return;
    const next = selectedSheets.includes(name) ? selectedSheets.filter((s) => s !== name) : [...selectedSheets, name];
    setSelectedSheets(next);
    if (file && formatKey) handlePreview(file, formatKey, columnMapping, next);
  };

  const handleCommit = async () => {
    if (!file || !formatKey) return;
    setUploadError(null);
    setCommitting(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('formatKey', formatKey);
      if (selectedBusinessId) form.append('businessId', selectedBusinessId);
      if (selectedTimePeriodId) form.append('timePeriodId', selectedTimePeriodId);
      if (Object.keys(columnMapping).length > 0) form.append('headerMapping', JSON.stringify(columnMapping));
      if (selectedSheets !== null) form.append('sheets', JSON.stringify(selectedSheets));
      const response = await fetch('/api/import-uploads/commit', { method: 'POST', headers: authHeaders(), body: form });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to commit import');
      closeUploadDialog();
      await fetchBatches();
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setCommitting(false);
    }
  };

  const openEditSeason = async (e: React.MouseEvent, b: ImportBatch) => {
    e.stopPropagation(); // don't also trigger the row's onClick (opens the review grid)
    setSeasonError(null);
    setEditingSeasonBatch(b);
    setEditingSeasonTimePeriodId(b.defaultTimePeriodId || null);
    if (timePeriods.length === 0) {
      try {
        const res = await fetch('/api/time-periods', { headers: authHeaders() });
        if (res.ok) setTimePeriods(await res.json());
      } catch {
        // best-effort — dropdown just stays empty if this fails
      }
    }
  };

  const closeEditSeason = () => {
    setEditingSeasonBatch(null);
    setSeasonError(null);
  };

  const handleSaveSeason = async () => {
    if (!editingSeasonBatch) return;
    setSavingSeason(true);
    setSeasonError(null);
    try {
      const response = await fetch(`/api/import-uploads/${editingSeasonBatch.id}/season`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ timePeriodId: editingSeasonTimePeriodId }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update season');
      closeEditSeason();
      await fetchBatches();
    } catch (err) {
      setSeasonError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSavingSeason(false);
    }
  };

  const handleDeleteBatch = async () => {
    if (!deletingBatch) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const response = await fetch(`/api/import-uploads/${deletingBatch.id}`, { method: 'DELETE', headers: authHeaders() });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to delete import');
      }
      setDeletingBatch(null);
      await fetchBatches();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setDeleting(false);
    }
  };

  const openStageMissing = (batch: ImportBatch) => {
    setStagingBatch(batch);
    setStageFile(null);
    setStageResult(null);
    setStageError(null);
  };

  const runStageMissing = async (batch: ImportBatch, chosen: File, dryRun: boolean) => {
    setStageBusy(true);
    setStageError(null);
    try {
      const form = new FormData();
      form.append('file', chosen);
      form.append('dryRun', String(dryRun));
      const response = await fetch(`/api/import-uploads/${batch.id}/stage-missing`, { method: 'POST', headers: authHeaders(), body: form });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Failed to read the file');
      setStageResult(data);
      if (!dryRun) await fetchBatches();
    } catch (err) {
      setStageError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setStageBusy(false);
    }
  };

  const canCommit = !!file && !!formatKey && !!preview && preview.rowCount > 0 && (!preview.requiresBusinessSelection || !!selectedBusinessId);

  if (loading && batches.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={1.5} mb={2}>
        <Typography variant="h6" display="flex" alignItems="center" gap={1}>
          <AssignmentIcon />
          Data Import
        </Typography>
        <Button variant="contained" startIcon={<UploadFileIcon />} onClick={openUploadDialog}>
          Upload File
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {timePeriodFilterOptions.length > 0 && (
        <Box display="flex" flexWrap="wrap" alignItems="center" columnGap={1} mb={1}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
            Time Period:
          </Typography>
          {timePeriodFilterOptions.map((o) => {
            const key = o.timePeriodId ?? NO_TIME_PERIOD;
            return (
              <FormControlLabel
                key={key}
                control={<Checkbox size="small" checked={checkedTimePeriodKeys.includes(key)} onChange={() => toggleTimePeriodFilter(key)} />}
                label={`${o.name} (${o.batchCount})`}
              />
            );
          })}
          {checkedTimePeriodKeys.length > 0 && (
            <Button size="small" onClick={() => setCheckedTimePeriodKeys([])}>
              Show all
            </Button>
          )}
        </Box>
      )}

      {batches.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {checkedTimePeriodKeys.length > 0
              ? 'No imports in the ticked time periods.'
              : 'No import batches yet — upload a file to get started.'}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ '& .MuiTableCell-root': { py: 1 } }}>
            <TableHead>
              <TableRow>
                <TableCell>File</TableCell>
                <TableCell>Format</TableCell>
                <TableCell>Business</TableCell>
                <TableCell>Time Period</TableCell>
                <TableCell>Uploaded</TableCell>
                <TableCell align="right">Rows</TableCell>
                <TableCell align="right">Matched</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {batches.map((b) => (
                <TableRow key={b.id} hover sx={{ cursor: 'pointer' }} onClick={() => setReviewingBatch(b)}>
                  <TableCell>{b.fileName}</TableCell>
                  <TableCell>{b.formatKey}</TableCell>
                  <TableCell>{b.businessName || '-'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {b.defaultTimePeriodName || '-'}
                      <IconButton size="small" onClick={(e) => openEditSeason(e, b)} title="Assign a time period to this file">
                        <EditIcon fontSize="inherit" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>{new Date(b.uploadedAtUtc).toLocaleString()}</TableCell>
                  <TableCell align="right">{b.rowCount}</TableCell>
                  <TableCell align="right" sx={{ minWidth: 90 }}>
                    <Tooltip title={`${b.matchedCount.toLocaleString()} of ${b.stagedCount.toLocaleString()} rows matched`}>
                      <Box>
                        {formatMatchedPercent(b.matchedCount, b.stagedCount)}
                        <LinearProgress
                          variant="determinate"
                          value={b.stagedCount ? (100 * b.matchedCount) / b.stagedCount : 0}
                          color={b.stagedCount > 0 && b.matchedCount === b.stagedCount ? 'success' : 'primary'}
                          sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
                        />
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip label={b.status} size="small" color={b.status === 'Committed' ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); openStageMissing(b); }}
                      title="Stage rows from this file that aren't staged yet"
                    >
                      <PlaylistAddIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => { e.stopPropagation(); setDeleteError(null); setDeletingBatch(b); }}
                      title="Delete this import"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {reviewingBatch && (
        <ImportBatchReviewGrid
          batch={reviewingBatch}
          open={!!reviewingBatch}
          onClose={() => { setReviewingBatch(null); fetchBatches(); }}
          onApproved={fetchBatches}
        />
      )}

      <Dialog open={!!editingSeasonBatch} onClose={closeEditSeason} maxWidth="xs" fullWidth>
        <DialogTitle>Assign Time Period</DialogTitle>
        <DialogContent>
          {seasonError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSeasonError(null)}>{seasonError}</Alert>}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Applies to every still-unmatched row in <strong>{editingSeasonBatch?.fileName}</strong> that has no time
            period of its own in the source file.
          </Typography>
          <Autocomplete
            options={timePeriods}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={timePeriods.find((t) => t.id === editingSeasonTimePeriodId) || null}
            onChange={(_, value) => setEditingSeasonTimePeriodId(value?.id || null)}
            renderInput={(params) => <TextField {...params} label="Time Period" />}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditSeason} disabled={savingSeason}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveSeason} disabled={savingSeason}>
            {savingSeason ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deletingBatch} onClose={() => setDeletingBatch(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Import</DialogTitle>
        <DialogContent>
          {deleteError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDeleteError(null)}>{deleteError}</Alert>}
          <Typography variant="body2">
            Permanently delete <strong>{deletingBatch?.fileName}</strong> and its {deletingBatch?.rowCount ?? 0} staged
            row(s)? Any records already created from matched rows in this file are kept — this only removes the
            upload/staging history. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingBatch(null)} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDeleteBatch} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!stagingBatch} onClose={() => !stageBusy && setStagingBatch(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Stage Missing Rows</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Choose <strong>{stagingBatch?.fileName}</strong> again. Rows already in this import are left as they are
            (matched or not); only rows it doesn't have yet are added, e.g. rows an earlier version of the import
            skipped. A different file is refused.
          </Typography>
          <input
            ref={stageFileRef}
            type="file"
            accept=".xlsx"
            hidden
            onChange={(e) => {
              const chosen = e.target.files?.[0];
              e.target.value = '';
              if (chosen && stagingBatch) {
                setStageFile(chosen);
                setStageResult(null);
                runStageMissing(stagingBatch, chosen, true);
              }
            }}
          />
          <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => stageFileRef.current?.click()} disabled={stageBusy}>
            {stageFile ? stageFile.name : 'Choose file'}
          </Button>
          {stageBusy && <Box display="flex" alignItems="center" gap={1} mt={2}><CircularProgress size={18} /><Typography variant="body2">Reading the file…</Typography></Box>}
          {stageError && <Alert severity="error" sx={{ mt: 2 }}>{stageError}</Alert>}
          {stageResult && !stageBusy && (
            stageResult.staged > 0 ? (
              <Alert severity="success" sx={{ mt: 2 }}>
                Staged {stageResult.staged.toLocaleString()} row(s). They're in the review as unmatched — re-run Auto Fill if
                you used it on this import.
              </Alert>
            ) : stageResult.missing === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                Nothing to add: all {stageResult.alreadyStaged.toLocaleString()} rows in this file are already staged.
              </Alert>
            ) : (
              <Box mt={2}>
                <Typography variant="body2">
                  {stageResult.alreadyStaged.toLocaleString()} rows are already staged.{' '}
                  <strong>{stageResult.missing.toLocaleString()}</strong> aren't yet:
                </Typography>
                <Table size="small" sx={{ mt: 1 }}>
                  <TableBody>
                    {Object.entries(stageResult.missingByCategory).map(([category, count]) => (
                      <TableRow key={category}>
                        <TableCell>{category}</TableCell>
                        <TableCell align="right">{count.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStagingBatch(null)} disabled={stageBusy}>
            {stageResult?.staged ? 'Close' : 'Cancel'}
          </Button>
          {stageResult && stageResult.missing > 0 && stageResult.staged === 0 && (
            <Button
              variant="contained"
              disabled={stageBusy}
              onClick={() => stagingBatch && stageFile && runStageMissing(stagingBatch, stageFile, false)}
            >
              Stage {stageResult.missing.toLocaleString()} row(s)
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={uploadOpen} onClose={closeUploadDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Upload Import File
          <IconButton size="small" onClick={closeUploadDialog}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Source Format</InputLabel>
              <Select value={formatKey} label="Source Format" onChange={(e) => handleFormatChange(e.target.value)}>
                {formats.map((f) => (
                  <MenuItem key={f.formatKey} value={f.formatKey}>{f.displayName}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Paper
              variant="outlined"
              sx={{
                p: 3, textAlign: 'center', cursor: 'pointer',
                borderStyle: 'dashed',
                borderColor: dragOver ? 'primary.main' : 'divider',
              }}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) handleFileChosen(f); }}
              onClick={() => fileRef.current?.click()}
            >
              <input ref={fileRef} type="file" accept=".xlsx" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileChosen(f); }} />
              <UploadFileIcon sx={{ fontSize: 32, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body2">{file ? file.name : 'Click or drag a .xlsx file here'}</Typography>
            </Paper>

            {uploadError && <Alert severity="error">{uploadError}</Alert>}
            {previewing && <Box display="flex" justifyContent="center"><CircularProgress size={24} /></Box>}

            {preview && (
              <Box>
                {preview.sheets.length > 1 && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2">Sheets to bring in</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Each sheet's name becomes the Farm, and each planting a Field (variety and hectares).
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 1 }}>
                      {preview.sheets.map((s) => (
                        <FormControlLabel
                          key={s.name}
                          sx={{ m: 0 }}
                          control={
                            <Checkbox
                              size="small"
                              checked={selectedSheets?.includes(s.name) ?? s.selected}
                              disabled={previewing || s.rowCount === 0}
                              onChange={() => handleSheetToggle(s.name)}
                            />
                          }
                          label={
                            <Typography variant="body2">
                              {s.name} ({s.rowCount})
                              {s.looksCombined && (
                                <Typography component="span" variant="caption" color="text.secondary"> — the other sheets put together</Typography>
                              )}
                            </Typography>
                          }
                        />
                      ))}
                    </Box>
                  </Box>
                )}
                <Typography variant="body2" gutterBottom>
                  <strong>{preview.rowCount}</strong> row(s) parsed.
                </Typography>
                {preview.warnings.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    {preview.warnings.slice(0, 5).join(' | ')}
                    {preview.warnings.length > 5 && ` (+${preview.warnings.length - 5} more)`}
                  </Alert>
                )}
                <Typography variant="caption" color="text.secondary" display="block">
                  Gene Stack values found: {preview.distinctGeneStackValues.join(', ') || 'none'}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Categories found: {preview.distinctProductCategoryValues.join(', ') || 'none'}
                </Typography>

                {preview.missingHeaders.length > 0 && (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Couldn't find these expected columns in the file — pick which column each one actually came from.
                      This mapping is remembered for future uploads of this format.
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {preview.missingHeaders.map((mh) => (
                        <FormControl key={mh.expectedHeader} size="small" fullWidth>
                          <InputLabel>{mh.label}</InputLabel>
                          <Select
                            value={columnMapping[mh.expectedHeader] || ''}
                            label={mh.label}
                            onChange={(e) => handleColumnMappingChange(mh.expectedHeader, e.target.value)}
                          >
                            <MenuItem value=""><em>Leave unmapped</em></MenuItem>
                            {preview.availableHeaders.map((h) => (
                              <MenuItem key={h} value={h}>{h}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ))}
                    </Box>
                  </Alert>
                )}

                {preview.requiresBusinessSelection && (
                  <Autocomplete
                    sx={{ mt: 2 }}
                    options={businesses}
                    getOptionLabel={(o) => o.name}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    value={businesses.find((b) => b.id === selectedBusinessId) || null}
                    onChange={(_, value) => setSelectedBusinessId(value?.id || null)}
                    renderInput={(params) => (
                      <TextField {...params} label="Business (required)" helperText="This file has no business identity of its own — every row will be attributed to this business." />
                    )}
                  />
                )}

                <Autocomplete
                  sx={{ mt: 2 }}
                  options={timePeriods}
                  getOptionLabel={(o) => o.name}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={timePeriods.find((t) => t.id === selectedTimePeriodId) || null}
                  onChange={(_, value) => setSelectedTimePeriodId(value?.id || null)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Time Period (optional)"
                      helperText="Fills in every row that doesn't already have one of its own in the file, instead of picking it again on each row in the wizard."
                    />
                  )}
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeUploadDialog}>Cancel</Button>
          <Button variant="contained" disabled={!canCommit || committing} onClick={handleCommit}>
            {committing ? 'Uploading...' : 'Commit Import'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DataImportManagement;
